import { ProviderInterface } from 'types';

/** Matches `defaultProvider.id` from constant/common ("Any Provider"). */
const ANY_PROVIDER_ID = -1;

export const getRealProviders = (
  providers: ProviderInterface[] | undefined
): ProviderInterface[] =>
  (providers ?? []).filter((provider) => provider.id !== ANY_PROVIDER_ID);

export type ProviderStepDecision =
  | { action: 'skip' }
  | { action: 'show' }
  | {
      action: 'autoSelect';
      selections: Array<{ entityId: number; provider: ProviderInterface }>;
    };

/**
 * Decide whether the provider step should be shown, skipped, or auto-completed.
 * - skip: every entity only has "Any" (id -1) / empty lists
 * - autoSelect: at least one real provider exists, and no entity has more than one
 * - show: any entity has 2+ real providers to choose from
 */
export const resolveProviderStepDecision = (
  entityIds: number[],
  providersByEntityId: Record<number, ProviderInterface[] | undefined>
): ProviderStepDecision => {
  const uniqueIds = Array.from(new Set(entityIds));
  const realByEntity = uniqueIds.map((entityId) => ({
    entityId,
    realProviders: getRealProviders(providersByEntityId[entityId])
  }));

  const hasAnyReal = realByEntity.some(
    ({ realProviders }) => realProviders.length > 0
  );

  if (!hasAnyReal) {
    return { action: 'skip' };
  }

  if (realByEntity.some(({ realProviders }) => realProviders.length > 1)) {
    return { action: 'show' };
  }

  return {
    action: 'autoSelect',
    selections: realByEntity
      .filter(({ realProviders }) => realProviders.length === 1)
      .map(({ entityId, realProviders }) => ({
        entityId,
        provider: realProviders[0]
      }))
  };
};
